import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createWorkspaceTraining, getWorkspaceTraining, updateWorkspaceTraining } from "../../../../services/workspaceApi";
import { ModuleTabStrip } from "../../common/WorkspaceChrome";

const initialRequestDraft = {
  title: "",
  requestMode: "lyriclious",
  trainerField: "",
  skills: "",
  audience: "",
  preferredSchedule: "",
  budget: "",
  description: "",
};
const initialEngagementDraft = {
  title: "",
  trainerName: "",
  audience: "",
  value: "",
  description: "",
};
const initialSessionDraft = {
  itemId: "",
  title: "",
  startsAt: "",
  endsAt: "",
  mode: "live",
  trainerName: "",
  attendeeNames: "",
  meetingUrl: "",
  recordingUrl: "",
  notes: "",
};
const initialAssetDraft = {
  itemId: "",
  title: "",
  name: "",
  kind: "link",
  url: "",
  visibility: "engagement",
  topic: "",
  description: "",
};
const initialFeedbackDraft = {
  itemId: "",
  authorName: "",
  audience: "client",
  rating: "5",
  comments: "",
  outcome: "",
};

const requestStatuses = ["all", "new", "qualified", "proposal_sent", "approved", "rejected", "cancelled"];
const formatStatus = (value = "") => `${value || "draft"}`.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
const money = (amount = 0, currency = "INR") =>
  `${currency === "INR" ? "₹" : `${currency} `}${Number(amount || 0).toLocaleString("en-IN")}`;
const toList = (value = "") => `${value || ""}`.split(/[\n,]/).map((item) => item.trim()).filter(Boolean);
const itemId = (item = {}) => `${item.id || item._id || ""}`;
const sessionId = (session = {}) => `${session.id || session._id || ""}`;
const formatDateTime = (value) => {
  if (!value) return "Not scheduled";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return `${value}`;
  return date.toLocaleString([], { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
};
const isUpcoming = (value) => {
  const date = new Date(value);
  return !Number.isNaN(date.getTime()) && date.getTime() >= Date.now();
};
const sameWeek = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;
  const now = new Date();
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 7);
  return date >= start && date <= end;
};

export function TrainingDeliveryView({
  activeTab,
  moduleTabs,
  setActiveTab,
  currentUser,
  workspaceInfo,
  selectedProjectName = "",
  selectedProjectId = "",
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [requestDraft, setRequestDraft] = useState(initialRequestDraft);
  const [engagementDraft, setEngagementDraft] = useState(initialEngagementDraft);
  const [sessionDraft, setSessionDraft] = useState(initialSessionDraft);
  const [assetDraft, setAssetDraft] = useState(initialAssetDraft);
  const [feedbackDraft, setFeedbackDraft] = useState(initialFeedbackDraft);
  const [requestStatusFilter, setRequestStatusFilter] = useState("all");

  const projectLabel = selectedProjectName || "Workspace-wide";
  const requesterContact = currentUser?.email || currentUser?.user?.email || "";
  const requesterName = currentUser?.name || currentUser?.user_name || currentUser?.user?.user_name || "Workspace member";

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getWorkspaceTraining();
      setItems(Array.isArray(data.items) ? data.items : []);
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "Could not load training delivery.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const upsertItems = useCallback((...incomingItems) => {
    const incoming = incomingItems.filter(Boolean);
    if (!incoming.length) return;
    setItems((current) => {
      const next = [...current];
      incoming.forEach((entry) => {
        const id = itemId(entry);
        const index = next.findIndex((existing) => itemId(existing) === id);
        if (index >= 0) next[index] = entry;
        else next.unshift(entry);
      });
      return next;
    });
  }, []);

  const projectItems = useMemo(() => items.filter((item) => {
    if (!selectedProjectName && !selectedProjectId) return true;
    const linkedProject = `${item.linked_project || ""}`;
    return !item.scope_title
      || item.scope_title === selectedProjectName
      || linkedProject === `${selectedProjectId}`;
  }), [items, selectedProjectId, selectedProjectName]);
  const requests = useMemo(() => projectItems.filter((item) => item.kind === "request"), [projectItems]);
  const externalRequests = useMemo(() => requests.filter((item) => item.requires_lyriclious_trainer), [requests]);
  const engagements = useMemo(() => projectItems.filter((item) => item.kind === "engagement"), [projectItems]);
  const libraryItems = useMemo(() => projectItems.filter((item) => item.kind === "library_asset"), [projectItems]);
  const sessions = useMemo(() => engagements.flatMap((item) => (item.sessions || []).map((session) => ({ item, session }))), [engagements]);
  const assets = useMemo(() => projectItems.flatMap((item) => (item.assets || []).map((asset) => ({ item, asset }))), [projectItems]);
  const feedback = useMemo(() => projectItems.flatMap((item) => (item.feedback || []).map((entry) => ({ item, feedback: entry }))), [projectItems]);
  const visibleRequests = useMemo(() => requests.filter((item) => requestStatusFilter === "all" || item.status === requestStatusFilter), [requestStatusFilter, requests]);
  const averageRating = feedback.length
    ? (feedback.reduce((total, entry) => total + Number(entry.feedback.rating || 0), 0) / feedback.length).toFixed(1)
    : "0";
  const selectableEngagements = engagements;

  const updateRequestDraft = (field) => (event) => setRequestDraft((current) => ({ ...current, [field]: event.target.value }));
  const updateEngagementDraft = (field) => (event) => setEngagementDraft((current) => ({ ...current, [field]: event.target.value }));
  const updateSessionDraft = (field) => (event) => setSessionDraft((current) => ({ ...current, [field]: event.target.value }));
  const updateAssetDraft = (field) => (event) => setAssetDraft((current) => ({ ...current, [field]: event.target.value }));
  const updateFeedbackDraft = (field) => (event) => setFeedbackDraft((current) => ({ ...current, [field]: event.target.value }));

  const submitRequest = async (event) => {
    event.preventDefault();
    if (!requestDraft.title.trim()) return;
    setSaving(true);
    setError("");
    try {
      const requiresLyricliousTrainer = requestDraft.requestMode === "lyriclious";
      const payload = {
        kind: "request",
        title: requestDraft.title.trim(),
        status: "new",
        source: requiresLyricliousTrainer ? "client" : "internal",
        clientName: workspaceInfo?.name || projectLabel,
        clientContact: requesterContact || requesterName,
        audience: requestDraft.audience,
        preferredSchedule: requestDraft.preferredSchedule,
        description: requestDraft.description,
        skillRequirements: toList(requestDraft.skills || requestDraft.trainerField),
        requiresLyricliousTrainer,
        trainerField: requestDraft.trainerField,
        budget: Number(requestDraft.budget || 0),
        currency: "INR",
        scopeType: selectedProjectName ? "project" : "standalone",
        scopeTitle: selectedProjectName,
        projectId: selectedProjectId,
        moduleName: "Training",
      };
      const data = await createWorkspaceTraining(payload);
      upsertItems(data.item);
      setRequestDraft(initialRequestDraft);
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "Could not create training request.");
    } finally {
      setSaving(false);
    }
  };

  const submitEngagement = async (event) => {
    event.preventDefault();
    if (!engagementDraft.title.trim()) return;
    setSaving(true);
    setError("");
    try {
      const data = await createWorkspaceTraining({
        kind: "engagement",
        title: engagementDraft.title.trim(),
        status: "active",
        source: "internal",
        clientName: workspaceInfo?.name || projectLabel,
        clientContact: requesterContact || requesterName,
        audience: engagementDraft.audience,
        description: engagementDraft.description,
        trainerName: engagementDraft.trainerName,
        value: Number(engagementDraft.value || 0),
        currency: "INR",
        scopeType: selectedProjectName ? "project" : "standalone",
        scopeTitle: selectedProjectName,
        projectId: selectedProjectId,
      });
      upsertItems(data.item);
      setEngagementDraft(initialEngagementDraft);
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "Could not create training engagement.");
    } finally {
      setSaving(false);
    }
  };

  const submitSession = async (event) => {
    event.preventDefault();
    const targetId = sessionDraft.itemId || itemId(selectableEngagements[0]);
    if (!targetId || !sessionDraft.title.trim() || !sessionDraft.startsAt) return;
    setSaving(true);
    setError("");
    try {
      const data = await updateWorkspaceTraining(targetId, {
        addSession: {
          title: sessionDraft.title.trim(),
          startsAt: sessionDraft.startsAt,
          endsAt: sessionDraft.endsAt,
          mode: sessionDraft.mode,
          trainerName: sessionDraft.trainerName,
          attendeeNames: toList(sessionDraft.attendeeNames),
          meetingUrl: sessionDraft.meetingUrl,
          recordingUrl: sessionDraft.recordingUrl,
          notes: sessionDraft.notes,
        },
      });
      upsertItems(data.item);
      setSessionDraft(initialSessionDraft);
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "Could not schedule training session.");
    } finally {
      setSaving(false);
    }
  };

  const submitAsset = async (event) => {
    event.preventDefault();
    const name = (assetDraft.name || assetDraft.title).trim();
    if (!name || !assetDraft.url.trim()) return;
    setSaving(true);
    setError("");
    try {
      let targetId = assetDraft.itemId;
      if (!targetId) {
        const created = await createWorkspaceTraining({
          kind: "library_asset",
          title: assetDraft.title || name,
          status: "published",
          source: "internal",
          description: assetDraft.description,
          scopeType: selectedProjectName ? "project" : "standalone",
          scopeTitle: selectedProjectName,
          projectId: selectedProjectId,
        });
        targetId = itemId(created.item);
        upsertItems(created.item);
      }
      const data = await updateWorkspaceTraining(targetId, {
        addAsset: {
          name,
          kind: assetDraft.kind,
          url: assetDraft.url,
          visibility: assetDraft.visibility,
          topic: assetDraft.topic,
        },
      });
      upsertItems(data.item);
      setAssetDraft(initialAssetDraft);
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "Could not save training material.");
    } finally {
      setSaving(false);
    }
  };

  const submitFeedback = async (event) => {
    event.preventDefault();
    const targetId = feedbackDraft.itemId || itemId(engagements[0]);
    if (!targetId || !feedbackDraft.comments.trim()) return;
    setSaving(true);
    setError("");
    try {
      const data = await updateWorkspaceTraining(targetId, {
        addFeedback: {
          authorName: feedbackDraft.authorName || requesterName,
          audience: feedbackDraft.audience,
          rating: Number(feedbackDraft.rating || 0),
          comments: feedbackDraft.comments,
          outcome: feedbackDraft.outcome,
        },
      });
      upsertItems(data.item);
      setFeedbackDraft(initialFeedbackDraft);
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "Could not save feedback.");
    } finally {
      setSaving(false);
    }
  };

  const acceptQuote = async (item) => {
    setSaving(true);
    setError("");
    try {
      const data = await updateWorkspaceTraining(itemId(item), { clientDecision: "accepted", createEngagement: true });
      upsertItems(data.item, data.engagement);
      setActiveTab("Engagements");
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "Could not accept trainer quote.");
    } finally {
      setSaving(false);
    }
  };

  const rejectQuote = async (item) => {
    setSaving(true);
    setError("");
    try {
      const data = await updateWorkspaceTraining(itemId(item), { clientDecision: "rejected", decisionNotes: "Rejected by client workspace." });
      upsertItems(data.item);
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "Could not reject trainer quote.");
    } finally {
      setSaving(false);
    }
  };

  const updateSessionStatus = async (entry, status) => {
    setSaving(true);
    setError("");
    try {
      const data = await updateWorkspaceTraining(itemId(entry.item), {
        updateSession: { sessionId: sessionId(entry.session), status },
      });
      upsertItems(data.item);
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "Could not update session.");
    } finally {
      setSaving(false);
    }
  };

  const prefillSession = (item) => {
    setSessionDraft((current) => ({ ...current, itemId: itemId(item), title: `${item.title} session` }));
    setActiveTab("Sessions");
  };

  const prefillAsset = (item) => {
    setAssetDraft((current) => ({ ...current, itemId: itemId(item), title: `${item.title} material` }));
    setActiveTab("Content Library");
  };

  const prefillFeedback = (item) => {
    setFeedbackDraft((current) => ({ ...current, itemId: itemId(item) }));
    setActiveTab("Feedback & Outcomes");
  };

  const prefillEngagementFromRequest = (item) => {
    setEngagementDraft({
      title: item.title || "",
      trainerName: "",
      audience: item.audience || "",
      value: "",
      description: item.description || "",
    });
    setActiveTab("Engagements");
  };

  const renderStatus = (status) => (
    <em className={`training-status is-${`${status || "draft"}`.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      {formatStatus(status)}
    </em>
  );

  const renderSkillChips = (skills = []) => (
    <div className="training-chip-row">
      {(skills || []).slice(0, 6).map((skill) => <span key={skill}>{skill}</span>)}
      {(skills || []).length > 6 ? <small>+{skills.length - 6}</small> : null}
    </div>
  );

  const renderRequestCard = (item) => {
    const quote = item.request || {};
    const hasQuote = item.status === "proposal_sent" || Number(quote.quote_amount || 0) > 0;
    return (
      <article className="training-card" key={itemId(item)}>
        <div className="training-card-main">
          <div className="training-card-heading">
            <span>{item.requires_lyriclious_trainer ? "Lyriclious trainer request" : "Internal training request"}</span>
            {renderStatus(item.status)}
          </div>
          <strong>{item.title}</strong>
          <small>{[item.client_name || "Internal", item.trainer_field || "No field set", item.scope_title || projectLabel].filter(Boolean).join(" · ")}</small>
          {renderSkillChips(item.skill_requirements)}
          <p>{item.audience || item.description || "No audience details added."}</p>
        </div>
        {hasQuote ? (
          <section className="training-quote-box">
            <div><span>Quote</span><strong>{money(quote.quote_amount, quote.quote_currency || quote.currency)}</strong></div>
            <p>{quote.proposed_schedule || "Schedule will be confirmed after acceptance."}</p>
            <small>{[quote.proposed_trainer_name, quote.proposal_requirements, quote.quote_notes].filter(Boolean).join(" · ")}</small>
            {item.status === "proposal_sent" ? (
              <div className="training-actions">
                <button disabled={saving} onClick={() => acceptQuote(item)} type="button">Accept quote</button>
                <button disabled={saving} onClick={() => rejectQuote(item)} type="button">Reject</button>
              </div>
            ) : null}
          </section>
        ) : null}
        <div className="training-actions">
          {!item.requires_lyriclious_trainer ? <button onClick={() => prefillEngagementFromRequest(item)} type="button">Create engagement</button> : null}
        </div>
      </article>
    );
  };

  const renderEngagementCard = (item) => (
    <article className="training-card" key={itemId(item)}>
      <div className="training-card-main">
        <div className="training-card-heading">
          <span>{item.requires_lyriclious_trainer ? "Lyriclious delivery" : "Team training"}</span>
          {renderStatus(item.engagement?.delivery_status || item.status)}
        </div>
        <strong>{item.title}</strong>
        <small>{[item.engagement?.trainer_name || item.engagement?.trainer?.name || "Trainer not assigned", item.audience, item.scope_title || projectLabel].filter(Boolean).join(" · ")}</small>
        {renderSkillChips(item.skill_requirements)}
      </div>
      <div className="training-card-stats">
        <span>{item.sessions?.length || 0}<small>sessions</small></span>
        <span>{item.assets?.length || 0}<small>assets</small></span>
        <span>{item.feedback?.length || 0}<small>feedback</small></span>
      </div>
      <div className="training-actions">
        <button onClick={() => prefillSession(item)} type="button">Schedule session</button>
        <button onClick={() => prefillAsset(item)} type="button">Add material</button>
        <button onClick={() => prefillFeedback(item)} type="button">Add feedback</button>
      </div>
    </article>
  );

  const renderSessionCard = (entry) => (
    <article className="training-card" key={`${itemId(entry.item)}-${sessionId(entry.session)}`}>
      <div className="training-card-main">
        <div className="training-card-heading">
          <span>{entry.session.mode || "live"} session</span>
          {renderStatus(entry.session.status)}
        </div>
        <strong>{entry.session.title}</strong>
        <small>{[entry.item.title, formatDateTime(entry.session.starts_at), entry.session.trainer_name || entry.item.engagement?.trainer_name].filter(Boolean).join(" · ")}</small>
        <p>{entry.session.notes || entry.session.meeting_url || "No session notes added."}</p>
      </div>
      <div className="training-actions">
        {entry.session.meeting_url ? <a href={entry.session.meeting_url} rel="noreferrer" target="_blank">Open link</a> : null}
        {entry.session.recording_url ? <a href={entry.session.recording_url} rel="noreferrer" target="_blank">Recording</a> : null}
        {entry.session.status !== "completed" ? <button disabled={saving} onClick={() => updateSessionStatus(entry, "completed")} type="button">Complete</button> : null}
        {entry.session.status !== "cancelled" ? <button disabled={saving} onClick={() => updateSessionStatus(entry, "cancelled")} type="button">Cancel</button> : null}
      </div>
    </article>
  );

  const renderAssetCard = (entry) => (
    <article className="training-card" key={`${itemId(entry.item)}-${entry.asset._id || entry.asset.url}`}>
      <div className="training-card-main">
        <div className="training-card-heading">
          <span>{entry.asset.kind || "asset"}</span>
          {renderStatus(entry.asset.visibility || "engagement")}
        </div>
        <strong>{entry.asset.name}</strong>
        <small>{[entry.item.title, entry.asset.topic, `v${entry.asset.version || 1}`].filter(Boolean).join(" · ")}</small>
      </div>
      <div className="training-actions">
        <a href={entry.asset.url} rel="noreferrer" target="_blank">Open</a>
      </div>
    </article>
  );

  const renderLibraryRecordCard = (item) => (
    <article className="training-card" key={itemId(item)}>
      <div className="training-card-main">
        <div className="training-card-heading">
          <span>Library record</span>
          {renderStatus(item.status || "published")}
        </div>
        <strong>{item.title}</strong>
        <small>{[item.scope_title || projectLabel, `${item.assets?.length || 0} linked assets`].filter(Boolean).join(" · ")}</small>
        {item.description ? <p>{item.description}</p> : null}
      </div>
      <div className="training-actions">
        <button onClick={() => prefillAsset(item)} type="button">Add asset</button>
      </div>
    </article>
  );

  const renderFeedbackCard = (entry) => (
    <article className="training-card" key={`${itemId(entry.item)}-${entry.feedback._id || entry.feedback.comments}`}>
      <div className="training-card-main">
        <div className="training-card-heading">
          <span>{entry.feedback.audience || "client"}</span>
          <em className="training-status is-approved">{entry.feedback.rating || "-"} / 5</em>
        </div>
        <strong>{entry.feedback.outcome || entry.item.title}</strong>
        <small>{[entry.feedback.author_name, entry.item.title].filter(Boolean).join(" · ")}</small>
        <p>{entry.feedback.comments}</p>
      </div>
    </article>
  );

  const renderOverview = () => {
    const pendingTrainerRequests = externalRequests.filter((item) => ["new", "qualified"].includes(item.status));
    const proposalRequests = externalRequests.filter((item) => item.status === "proposal_sent");
    const upcomingSessions = sessions.filter((entry) => isUpcoming(entry.session.starts_at));
    const weekSessions = sessions.filter((entry) => sameWeek(entry.session.starts_at));
    return (
      <section className="training-delivery-workspace">
        <section className="training-dashboard-grid">
          {[
            ["Trainer requests", pendingTrainerRequests.length, "Need Lyriclious quote"],
            ["Quotes waiting", proposalRequests.length, "Client decision pending"],
            ["Active engagements", engagements.filter((item) => !["completed", "cancelled"].includes(item.status)).length, "Delivery running/planned"],
            ["Sessions this week", weekSessions.length, "Scheduled occurrences"],
            ["Assets", assets.length, "Docs/videos/recordings"],
            ["Avg feedback", averageRating, "Quality score"],
          ].map(([label, value, note]) => (
            <article className="training-metric-card" key={label}><span>{label}</span><strong>{value}</strong><small>{note}</small></article>
          ))}
        </section>
        <section className="training-panel-grid">
          <section className="functional-panel">
            <div className="functional-panel-header"><div><span>Revenue queue</span><h3>Lyriclious trainer requests</h3></div><small>{pendingTrainerRequests.length + proposalRequests.length} active</small></div>
            <div className="training-card-list">{[...pendingTrainerRequests, ...proposalRequests].slice(0, 5).map(renderRequestCard)}{!pendingTrainerRequests.length && !proposalRequests.length ? <p className="empty-state">No active Lyriclious trainer request for this project.</p> : null}</div>
          </section>
          <section className="functional-panel">
            <div className="functional-panel-header"><div><span>Delivery</span><h3>Upcoming sessions</h3></div><small>{upcomingSessions.length} upcoming</small></div>
            <div className="training-card-list">{upcomingSessions.slice(0, 5).map(renderSessionCard)}{!upcomingSessions.length ? <p className="empty-state">No upcoming training sessions scheduled.</p> : null}</div>
          </section>
        </section>
      </section>
    );
  };

  const renderRequests = () => (
    <section className="training-delivery-workspace">
      <section className="functional-panel">
        <div className="functional-panel-header"><div><span>Training intake</span><h3>Raise or track training requests</h3></div><small>{requests.length} requests</small></div>
        <form className="training-create-form" onSubmit={submitRequest}>
          <label className="training-wide">Request title<input required value={requestDraft.title} onChange={updateRequestDraft("title")} placeholder="React performance training for frontend team" /></label>
          <label>Trainer source<select value={requestDraft.requestMode} onChange={updateRequestDraft("requestMode")}><option value="lyriclious">Need Lyriclious trainer</option><option value="internal">Internal/team trainer</option></select></label>
          <label>Field / topic<input value={requestDraft.trainerField} onChange={updateRequestDraft("trainerField")} placeholder="React, Node.js, DevOps, Sales, HR..." /></label>
          <label className="training-wide">Skills needed<input value={requestDraft.skills} onChange={updateRequestDraft("skills")} placeholder="Hooks, architecture, testing" /></label>
          <label>Audience<input value={requestDraft.audience} onChange={updateRequestDraft("audience")} placeholder="Frontend juniors, 8 people" /></label>
          <label>Preferred schedule<input value={requestDraft.preferredSchedule} onChange={updateRequestDraft("preferredSchedule")} placeholder="Next week, 2 sessions, evening" /></label>
          <label>Budget expectation<input min="0" type="number" value={requestDraft.budget} onChange={updateRequestDraft("budget")} placeholder="Optional" /></label>
          <label>Project<input disabled value={projectLabel} /></label>
          <label className="training-wide">Context<textarea rows={3} value={requestDraft.description} onChange={updateRequestDraft("description")} placeholder="What outcome is expected, current skill level, prerequisites, business reason." /></label>
          <button disabled={saving} type="submit">{saving ? "Saving..." : requestDraft.requestMode === "lyriclious" ? "Request Lyriclious trainer" : "Create internal request"}</button>
        </form>
      </section>
      <section className="functional-panel">
        <div className="functional-panel-header">
          <div><span>Request queue</span><h3>Client decisions and trainer quotes</h3></div>
          <select aria-label="Filter training requests" value={requestStatusFilter} onChange={(event) => setRequestStatusFilter(event.target.value)}>
            {requestStatuses.map((status) => <option key={status} value={status}>{formatStatus(status)}</option>)}
          </select>
        </div>
        <div className="training-card-list">{visibleRequests.map(renderRequestCard)}{!visibleRequests.length ? <p className="empty-state">No training requests match this status.</p> : null}</div>
      </section>
    </section>
  );

  const renderEngagements = () => (
    <section className="training-delivery-workspace">
      <section className="functional-panel">
        <div className="functional-panel-header"><div><span>Engagement setup</span><h3>Approved or internal training delivery</h3></div><small>{engagements.length} engagements</small></div>
        <form className="training-create-form" onSubmit={submitEngagement}>
          <label className="training-wide">Engagement title<input required value={engagementDraft.title} onChange={updateEngagementDraft("title")} placeholder="Frontend team enablement sprint" /></label>
          <label>Trainer / owner<input value={engagementDraft.trainerName} onChange={updateEngagementDraft("trainerName")} placeholder="Internal trainer or owner" /></label>
          <label>Commercial value<input min="0" type="number" value={engagementDraft.value} onChange={updateEngagementDraft("value")} placeholder="0 for internal" /></label>
          <label>Audience<input value={engagementDraft.audience} onChange={updateEngagementDraft("audience")} placeholder="Team, role, client group" /></label>
          <label>Project<input disabled value={projectLabel} /></label>
          <label className="training-wide">Delivery plan<textarea rows={3} value={engagementDraft.description} onChange={updateEngagementDraft("description")} placeholder="Scope, expected outcome, schedule structure, owner expectations." /></label>
          <button disabled={saving} type="submit">{saving ? "Saving..." : "Create engagement"}</button>
        </form>
      </section>
      <div className="training-card-list">{engagements.map(renderEngagementCard)}{!engagements.length ? <p className="empty-state">No engagements yet. Accept a Lyriclious quote or create internal training delivery.</p> : null}</div>
    </section>
  );

  const renderSessions = () => (
    <section className="training-delivery-workspace">
      <section className="functional-panel">
        <div className="functional-panel-header"><div><span>Sessions</span><h3>Schedule live, recorded, or hybrid training</h3></div><small>{sessions.length} sessions</small></div>
        <form className="training-create-form" onSubmit={submitSession}>
          <label className="training-wide">Engagement<select value={sessionDraft.itemId} onChange={updateSessionDraft("itemId")}><option value="">Select engagement</option>{selectableEngagements.map((item) => <option key={itemId(item)} value={itemId(item)}>{item.title}</option>)}</select></label>
          <label className="training-wide">Session title<input required value={sessionDraft.title} onChange={updateSessionDraft("title")} placeholder="Session 1 — Architecture foundations" /></label>
          <label>Start<input required type="datetime-local" value={sessionDraft.startsAt} onChange={updateSessionDraft("startsAt")} /></label>
          <label>End<input type="datetime-local" value={sessionDraft.endsAt} onChange={updateSessionDraft("endsAt")} /></label>
          <label>Mode<select value={sessionDraft.mode} onChange={updateSessionDraft("mode")}><option value="live">Live only</option><option value="recorded">Recorded only</option><option value="hybrid">Live with recording</option></select></label>
          <label>Trainer<input value={sessionDraft.trainerName} onChange={updateSessionDraft("trainerName")} placeholder="Trainer name" /></label>
          <label className="training-wide">Meeting link<input value={sessionDraft.meetingUrl} onChange={updateSessionDraft("meetingUrl")} placeholder="Google Meet, Zoom, Teams..." /></label>
          <label className="training-wide">Recording URL<input value={sessionDraft.recordingUrl} onChange={updateSessionDraft("recordingUrl")} placeholder="Required for recorded/hybrid after upload" /></label>
          <label className="training-wide">Attendees<textarea rows={2} value={sessionDraft.attendeeNames} onChange={updateSessionDraft("attendeeNames")} placeholder="One name/email per line or comma-separated" /></label>
          <label className="training-wide">Notes<textarea rows={3} value={sessionDraft.notes} onChange={updateSessionDraft("notes")} placeholder="Agenda, prerequisites, attendance notes, follow-up." /></label>
          <button disabled={saving || !selectableEngagements.length} type="submit">{saving ? "Saving..." : "Schedule session"}</button>
        </form>
      </section>
      <div className="training-card-list">{[...sessions].sort((left, right) => new Date(left.session.starts_at) - new Date(right.session.starts_at)).map(renderSessionCard)}{!sessions.length ? <p className="empty-state">No sessions scheduled yet.</p> : null}</div>
    </section>
  );

  const renderLibrary = () => (
    <section className="training-delivery-workspace">
      <section className="functional-panel">
        <div className="functional-panel-header"><div><span>Content library</span><h3>Docs, videos, recordings, and reusable material</h3></div><small>{assets.length} assets</small></div>
        <form className="training-create-form" onSubmit={submitAsset}>
          <label className="training-wide">Attach to<select value={assetDraft.itemId} onChange={updateAssetDraft("itemId")}><option value="">Standalone library item</option>{projectItems.map((item) => <option key={itemId(item)} value={itemId(item)}>{item.title}</option>)}</select></label>
          <label>Asset title<input value={assetDraft.title} onChange={updateAssetDraft("title")} placeholder="Reusable onboarding deck" /></label>
          <label>Name<input required value={assetDraft.name} onChange={updateAssetDraft("name")} placeholder="Deck, recording, worksheet" /></label>
          <label>Type<select value={assetDraft.kind} onChange={updateAssetDraft("kind")}><option value="link">Link</option><option value="document">Document</option><option value="video">Video</option><option value="recording">Recording</option></select></label>
          <label>Visibility<select value={assetDraft.visibility} onChange={updateAssetDraft("visibility")}><option value="engagement">Engagement only</option><option value="client">Client visible</option><option value="internal">Internal only</option></select></label>
          <label className="training-wide">URL<input required value={assetDraft.url} onChange={updateAssetDraft("url")} placeholder="https://..." /></label>
          <label>Topic<input value={assetDraft.topic} onChange={updateAssetDraft("topic")} placeholder="React basics" /></label>
          <label>Project<input disabled value={projectLabel} /></label>
          <label className="training-wide">Description<textarea rows={3} value={assetDraft.description} onChange={updateAssetDraft("description")} placeholder="When to use this asset and who can access it." /></label>
          <button disabled={saving} type="submit">{saving ? "Saving..." : "Save material"}</button>
        </form>
      </section>
      <div className="training-card-list">{assets.map(renderAssetCard)}{!assets.length ? <p className="empty-state">No training material linked yet.</p> : null}</div>
      {libraryItems.length ? <section className="functional-panel"><div className="functional-panel-header"><div><span>Standalone items</span><h3>Reusable library records</h3></div><small>{libraryItems.length} records</small></div><div className="training-card-list">{libraryItems.map(renderLibraryRecordCard)}</div></section> : null}
    </section>
  );

  const renderFeedback = () => (
    <section className="training-delivery-workspace">
      <section className="functional-panel">
        <div className="functional-panel-header"><div><span>Outcomes</span><h3>Feedback and completion signal</h3></div><small>{feedback.length} feedback entries</small></div>
        <form className="training-create-form" onSubmit={submitFeedback}>
          <label className="training-wide">Engagement<select value={feedbackDraft.itemId} onChange={updateFeedbackDraft("itemId")}><option value="">Select engagement</option>{selectableEngagements.map((item) => <option key={itemId(item)} value={itemId(item)}>{item.title}</option>)}</select></label>
          <label>Author<input value={feedbackDraft.authorName} onChange={updateFeedbackDraft("authorName")} placeholder="Client, participant, manager" /></label>
          <label>Audience<select value={feedbackDraft.audience} onChange={updateFeedbackDraft("audience")}><option value="client">Client</option><option value="participant">Participant</option><option value="internal">Internal</option></select></label>
          <label>Rating<select value={feedbackDraft.rating} onChange={updateFeedbackDraft("rating")}>{[5, 4, 3, 2, 1].map((rating) => <option key={rating} value={rating}>{rating}</option>)}</select></label>
          <label className="training-wide">Outcome<input value={feedbackDraft.outcome} onChange={updateFeedbackDraft("outcome")} placeholder="Completed, renewal possible, follow-up needed" /></label>
          <label className="training-wide">Feedback<textarea required rows={3} value={feedbackDraft.comments} onChange={updateFeedbackDraft("comments")} placeholder="What worked, what needs improvement, next action." /></label>
          <button disabled={saving || !selectableEngagements.length} type="submit">{saving ? "Saving..." : "Save feedback"}</button>
        </form>
      </section>
      <div className="training-card-list">{feedback.map(renderFeedbackCard)}{!feedback.length ? <p className="empty-state">No feedback captured yet.</p> : null}</div>
    </section>
  );

  const tabPanel = activeTab === "Overview"
    ? renderOverview()
    : activeTab === "Requests"
      ? renderRequests()
      : activeTab === "Engagements"
        ? renderEngagements()
        : activeTab === "Sessions"
          ? renderSessions()
          : activeTab === "Content Library"
            ? renderLibrary()
            : renderFeedback();

  return (
    <>
      <ModuleTabStrip moduleName="Training" activeTab={activeTab} setActiveTab={setActiveTab} tabs={moduleTabs} />
      <section className="functional-workspace training-module">
        <section className="functional-panel training-context-panel">
          <div>
            <span>Training delivery</span>
            <h3>{projectLabel}</h3>
            <p>Run internal team training, request Lyriclious trainers, accept quotes, schedule sessions, share material, and capture outcomes.</p>
          </div>
          <small>{loading ? "Loading…" : `${projectItems.length} records in this project context`}</small>
        </section>
        {error ? <p className="workspace-inline-error">{error}</p> : null}
        {loading ? <p className="empty-state">Loading training delivery…</p> : tabPanel}
      </section>
    </>
  );
}
